import { useMediaQuery, useTheme } from '@mui/material'
import { Avatar, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, TextField, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import * as React from 'react'
import { useNavigate } from 'react-router'

import { useUsers } from '../../contexts/UserContext'
import { getImage } from '../../data/Images'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import TripService from '../../services/TripService'
import { STATUS } from '../../share/Constant'
import useBar from '../common/Bar'
import EventDisplay from '../common/EventDisplay'
import WorkInProgress from '../common/WorkInProgress'

export default function TripCard({ trip, updateHook, deleteHook, mode = 'detailed' }) {
  const user = useAtomValue(currentUserAtom)
  const bar = useBar()
  const [registerDialogOpen, setRegisterDialogOpen] = React.useState(false)
  const [notes, setNotes] = React.useState('')
  const registerButtonRef = React.useRef(null)
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [editFormData, setEditFormData] = React.useState(null)
  const [editFormErrors, setEditFormErrors] = React.useState({})
  const [submitting, setSubmitting] = React.useState(false)
  const [unregisterDialogOpen, setUnregisterDialogOpen] = React.useState(false)
  const [selectedParticipants, setSelectedParticipants] = React.useState([])
  const { users, fetchUsers } = useUsers()
  const [loadingUsers, setLoadingUsers] = React.useState(false)
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleRegisterClick = () => {
    navigate(`/wip`)
    // setRegisterDialogOpen(true)
  }

  const handleRegisterClose = () => {
    if (registerButtonRef.current) {
      registerButtonRef.current.focus()
    }
    setRegisterDialogOpen(false)
    setNotes('')
  }

  const handleRegister = async () => {
    try {
      await TripService.registerTrip(trip.resourceId, {
        participantIds: [user.resourceId],
        notes: notes.trim(),
      })
      bar.success('Successfully registered for trip')
      handleRegisterClose()
      if (updateHook) {
        updateHook()
      }
    } catch (err) {
      bar.error('Failed to register for session: ' + err.message)
    }
  }

  const handleUnregisterClick = async () => {
    if (trip.participantIds?.length > 0) {
      setLoadingUsers(true)
      try {
        await fetchUsers({ userIds: trip.participantIds })
      } catch (err) {
        bar.error('Failed to load participants')
        return
      } finally {
        setLoadingUsers(false)
      }
    }
    setSelectedParticipants([])
    setUnregisterDialogOpen(true)
  }

  const handleUnregisterClose = () => {
    setUnregisterDialogOpen(false)
    setSelectedParticipants([])
  }

  const handleParticipantToggle = (participantId) => {
    setSelectedParticipants((prev) => {
      if (prev.includes(participantId)) {
        return prev.filter((id) => id !== participantId)
      } else {
        return [...prev, participantId]
      }
    })
  }

  const handleUnregisterConfirm = async () => {
    if (selectedParticipants.length === 0) {
      bar.warning('Please select participants to unregister')
      return
    }

    try {
      await TripService.unregisterTrip(trip.resourceId, {
        participantIds: selectedParticipants,
      })
      bar.success('Successfully unregistered participants')
      handleUnregisterClose()
      if (updateHook) {
        updateHook()
      }
    } catch (err) {
      console.error('Failed to unregister participants:', err)
      bar.error(err.message || 'Failed to unregister participants')
    }
  }

  const handleDelete = async () => {
    try {
      await TripService.deleteTrip(trip.resourceId)
      bar.success('Successfully deleted trip')
      if (deleteHook) {
        deleteHook()
      }
    } catch (err) {
      console.error('Failed to delete trip:', err)
      bar.error(err.message || 'Failed to delete trip')
    }
  }

  const handleEditClick = () => {
    navigate(`/wip`)
    return
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setEditFormData(null)
    setEditFormErrors({})
  }

  const validateEditForm = (data) => {
    const errors = {}
    if (!data.title) errors.title = 'Title is required'
    if (!data.sport) errors.sport = 'Sport type is required'
    if (!data.skiResort) errors.skiResort = 'Location is required'
    if (!data.price || data.price < 0) errors.price = 'Price must be greater than 0'
    if (!data.minParticipants || data.minParticipants < 1) errors.minParticipants = 'Minimum participants must be at least 1'
    if (!data.maxParticipants || data.maxParticipants < data.minParticipants) {
      errors.maxParticipants = 'Maximum participants must be greater than minimum'
    }
    return errors
  }

  const handleEditSubmit = async () => {
    navigate(`/wip`)
    return
    // const errors = validateEditForm(editFormData)
    // if (Object.keys(errors).length > 0) {
    //   setEditFormErrors(errors)
    //   bar.error('Please fix the errors before submitting')
    //   return
    // }

    // try {
    //   setSubmitting(true)
    //   await GroupSessionService.updateGroupSession(session.resourceId, editFormData)
    //   bar.success('Successfully updated session')
    //   handleEditClose()
    //   updateHook?.()
    // } catch (err) {
    //   console.error('Failed to update session:', err)
    //   bar.error(err.message || 'Failed to update session')
    // } finally {
    //   setSubmitting(false)
    // }
  }

  const editable = user?.roles?.includes('ADMIN') || trip.instructorId === user?.resourceId || trip.ownerId === user?.resourceId

  const actions = {
    viewable: false,
    editable: editable && trip.status !== STATUS.COMPLETED,
    unregisterable: editable && trip.participantIds?.length > 0 && trip.status !== STATUS.COMPLETED,
    deleteable: editable && trip.status !== STATUS.COMPLETED,
    onRegister: handleRegisterClick,
    onEdit: handleEditClick,
    onUnregister: handleUnregisterClick,
    onDelete: handleDelete,
  }

  actions.enabled = actions.editable || actions.unregisterable || actions.deleteable || actions.viewable

  const eventData = {
    ...trip,
    image: trip.image || getImage(trip.resourceId),
  }

  return (
    <>
      <EventDisplay event={eventData} mode={mode} actions={actions} link={`/events/trips/${trip.resourceId}`} />

      {/* Registration Dialog */}
      <Dialog
        open={registerDialogOpen}
        onClose={handleRegisterClose}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        keepMounted={false}
        aria-labelledby="registration-dialog-title"
        aria-describedby="registration-dialog-description"
      >
        <DialogTitle id="registration-dialog-title">Confirm Registration</DialogTitle>
        <DialogContent id="registration-dialog-description">
          {/* Session Details */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are about to book the following trip:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Date:</strong> {trip && dayjs(trip.date).format('MMM DD, YYYY')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Location:</strong> {trip.skiResort}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Session Notes */}
          <Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Session Notes: <br />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sending a notes to organizer
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              margin="normal"
              placeholder=""
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              aria-label="Session notes"
              sx={{
                mt: 3,
                '& .MuiInputBase-root': {
                  minHeight: '150px',
                },
                '& .MuiOutlinedInput-root': {
                  padding: '16px',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegisterClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleRegister} variant="contained" color="primary" autoFocus>
            Confirm Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="md" fullWidth disableRestoreFocus keepMounted={false} aria-labelledby="edit-dialog-title">
        <DialogTitle id="edit-dialog-title">Edit Trip</DialogTitle>
        <DialogContent>
          <WorkInProgress />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} color="inherit">
            Cancel
          </Button>
          <Button loading={submitting} loadingPosition="start" onClick={handleEditSubmit} variant="contained" color="info" disabled={submitting} size={'medium'}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unregister Dialog */}
      <Dialog open={unregisterDialogOpen} onClose={handleUnregisterClose} maxWidth="sm" fullWidth disableRestoreFocus keepMounted={false} aria-labelledby="unregister-dialog-title">
        <DialogTitle id="unregister-dialog-title">Unregister Participants</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Select participants to unregister:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              trip.participantIds?.map((participantId) => {
                const participant = users[participantId]
                return (
                  <FormControlLabel
                    key={participantId}
                    control={<Checkbox checked={selectedParticipants.includes(participantId)} onChange={() => handleParticipantToggle(participantId)} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={participant?.avatar} sx={{ width: 24, height: 24 }}>
                          {participant?.firstName?.[0]}
                        </Avatar>
                        <Typography>{participant ? `${participant.firstName} ${participant.lastName}` : 'Unknown User'}</Typography>
                      </Box>
                    }
                  />
                )
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnregisterClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleUnregisterConfirm} color="error" variant="contained" disabled={selectedParticipants.length === 0 || loadingUsers}>
            Confirm Unregister
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
